"use client";

import { LazyMotion, domAnimation, m } from "framer-motion";
import { PASSWORLD_MAP_EMBED_URL, PASSWORLD_MAP_TITLE } from "@/lib/map-embed";

export type MeetingItemVM = {
    _key: string;
    heading: string;
};

export type MeetingSectionClientProps = {
    heading: string;
    description: string;
    items: MeetingItemVM[];
    theme?: "light" | "dark";
};

const slideUp = {
    hidden: { y: 30, opacity: 0 },
    visible: (i: number) => ({
        y: 0,
        opacity: 1,
        transition: { duration: 0.8, ease: [0.215, 0.61, 0.355, 1], delay: i * 0.1 },
    }),
} as const;

export default function MeetingSectionClient({
    heading,
    description,
    items,
    theme = "light",
}: MeetingSectionClientProps) {
    const isDark = theme === "dark";

    return (
        <LazyMotion features={domAnimation}>
            <m.section
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.1 }}
                className={[
                    "px-[5%] py-16 md:py-24 lg:py-28",
                    isDark ? "bg-secondary text-primary" : "",
                ].join(" ")}
            >
                <div className="container">
                    <div className="grid grid-cols-1 items-center gap-12 md:grid-cols-2 lg:gap-x-20">
                        <m.div
                            variants={slideUp}
                            custom={0}
                            className="order-2 h-[400px] w-full min-w-0 overflow-hidden rounded-3xl md:order-1 md:h-[516px] will-change-transform"
                        >
                            <iframe
                                src={PASSWORLD_MAP_EMBED_URL}
                                width="100%"
                                height="100%"
                                style={{ border: 0 }}
                                allowFullScreen
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                                title={PASSWORLD_MAP_TITLE}
                                className="size-full"
                            />
                        </m.div>

                        <div className="order-1 md:order-2">
                            <m.h2
                                variants={slideUp}
                                custom={1}
                                className="mb-5 text-3xl font-medium md:mb-6 md:text-5xl will-change-transform"
                            >
                                {heading}
                            </m.h2>

                            <m.p
                                variants={slideUp}
                                custom={2}
                                className={[
                                    "mb-5 md:mb-6 md:text-md will-change-transform",
                                    isDark ? "text-primary/85" : "",
                                ].join(" ")}
                            >
                                {description}
                            </m.p>

                            {items.length > 0 && (
                                <m.div variants={slideUp} custom={3} className="my-4 will-change-transform">
                                    <ul
                                        className={[
                                            "my-4 list-disc pl-5",
                                            isDark ? "marker:text-primary/70" : "",
                                        ].join(" ")}
                                    >
                                        {items.map((item) => (
                                            <li key={item._key} className="my-1 self-start pl-2">
                                                <p className={isDark ? "text-primary/90" : undefined}>
                                                    {item.heading}
                                                </p>
                                            </li>
                                        ))}
                                    </ul>
                                </m.div>
                            )}
                        </div>
                    </div>
                </div>
            </m.section>
        </LazyMotion>
    );
}
